/**
 * 图片缓存工具类
 * 支持内存缓存、localStorage持久化、图片预加载
 */
class ImageCache {
  constructor() {
    // 内存缓存
    this.memoryCache = new Map()
    // localStorage缓存键前缀
    this.storagePrefix = 'img_cache_'
    // 缓存大小限制（MB）
    this.maxCacheSize = 50
    // 缓存过期时间（毫秒）
    this.expireTime = 7 * 24 * 60 * 60 * 1000 // 7天
    
    // 初始化时清理过期缓存
    this.cleanExpiredCache()
  }

  /**
   * 获取缓存的图片
   * @param {string} url 图片URL
   * @returns {Promise<string>} 返回图片的blob URL或原URL
   */
  async getImage(url) {
    if (!url) return null

    try {
      // 1. 先检查内存缓存
      if (this.memoryCache.has(url)) {
        return this.memoryCache.get(url)
      }

      // 2. 检查localStorage缓存
      const cachedData = this.getFromStorage(url)
      if (cachedData) {
        // 将数据恢复到内存缓存
        this.memoryCache.set(url, cachedData.blobUrl)
        return cachedData.blobUrl
      }

      // 3. 从网络加载图片
      const blobUrl = await this.loadAndCacheImage(url)
      return blobUrl
    } catch (error) {
      console.warn('图片缓存加载失败:', error)
      return url // 返回原URL作为fallback
    }
  }

  /**
   * 从网络加载图片并缓存
   * @param {string} url 图片URL
   * @returns {Promise<string>} blob URL
   */
  async loadAndCacheImage(url) {
    try {
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)

      // 存储到内存缓存
      this.memoryCache.set(url, blobUrl)

      // 存储到localStorage（转换为base64）
      const reader = new FileReader()
      reader.onloadend = () => {
        this.saveToStorage(url, {
          blobUrl,
          base64: reader.result,
          timestamp: Date.now(),
          size: blob.size
        })
      }
      reader.readAsDataURL(blob)

      return blobUrl
    } catch (error) {
      throw new Error(`图片加载失败: ${error.message}`)
    }
  }

  /**
   * 预加载图片列表
   * @param {Array<string>} urls 图片URL列表
   * @returns {Promise<Array>} 加载结果
   */
  async preloadImages(urls) {
    const promises = urls.map(url => 
      this.getImage(url).catch(error => {
        console.warn(`预加载图片失败 ${url}:`, error)
        return null
      })
    )
    
    return await Promise.allSettled(promises)
  }

  /**
   * 从localStorage获取缓存
   * @param {string} url 图片URL
   * @returns {Object|null} 缓存数据
   */
  getFromStorage(url) {
    try {
      const key = this.storagePrefix + this.hashUrl(url)
      const data = localStorage.getItem(key)
      
      if (!data) return null

      const parsed = JSON.parse(data)
      
      // 检查是否过期
      if (Date.now() - parsed.timestamp > this.expireTime) {
        localStorage.removeItem(key)
        return null
      }

      // 重新创建blob URL
      if (parsed.base64) {
        const response = fetch(parsed.base64)
        response.then(res => res.blob()).then(blob => {
          parsed.blobUrl = URL.createObjectURL(blob)
        })
      }

      return parsed
    } catch (error) {
      console.warn('读取localStorage缓存失败:', error)
      return null
    }
  }

  /**
   * 保存到localStorage
   * @param {string} url 图片URL
   * @param {Object} data 缓存数据
   */
  saveToStorage(url, data) {
    try {
      const key = this.storagePrefix + this.hashUrl(url)
      
      // 检查缓存大小限制
      if (this.getCurrentCacheSize() > this.maxCacheSize * 1024 * 1024) {
        this.cleanOldCache()
      }

      localStorage.setItem(key, JSON.stringify(data))
    } catch (error) {
      console.warn('保存localStorage缓存失败:', error)
      // localStorage可能已满，清理一些旧缓存
      this.cleanOldCache()
    }
  }

  /**
   * 清理过期缓存
   */
  cleanExpiredCache() {
    try {
      const keys = Object.keys(localStorage).filter(key => 
        key.startsWith(this.storagePrefix)
      )

      keys.forEach(key => {
        try {
          const data = JSON.parse(localStorage.getItem(key))
          if (Date.now() - data.timestamp > this.expireTime) {
            localStorage.removeItem(key)
          }
        } catch (error) {
          // 数据格式错误，直接删除
          localStorage.removeItem(key)
        }
      })
    } catch (error) {
      console.warn('清理过期缓存失败:', error)
    }
  }

  /**
   * 清理旧缓存（按时间排序，删除最旧的）
   */
  cleanOldCache() {
    try {
      const keys = Object.keys(localStorage).filter(key => 
        key.startsWith(this.storagePrefix)
      )

      const cacheItems = keys.map(key => {
        try {
          const data = JSON.parse(localStorage.getItem(key))
          return { key, timestamp: data.timestamp }
        } catch {
          return { key, timestamp: 0 }
        }
      })

      // 按时间排序，删除最旧的一半
      cacheItems.sort((a, b) => a.timestamp - b.timestamp)
      const toDelete = cacheItems.slice(0, Math.floor(cacheItems.length / 2))
      
      toDelete.forEach(item => {
        localStorage.removeItem(item.key)
      })
    } catch (error) {
      console.warn('清理旧缓存失败:', error)
    }
  }

  /**
   * 获取当前缓存大小
   * @returns {number} 字节数
   */
  getCurrentCacheSize() {
    try {
      let size = 0
      const keys = Object.keys(localStorage).filter(key => 
        key.startsWith(this.storagePrefix)
      )

      keys.forEach(key => {
        const data = localStorage.getItem(key)
        if (data) {
          size += data.length * 2 // 每个字符2字节
        }
      })

      return size
    } catch (error) {
      return 0
    }
  }

  /**
   * URL哈希函数
   * @param {string} url 图片URL
   * @returns {string} 哈希值
   */
  hashUrl(url) {
    let hash = 0
    for (let i = 0; i < url.length; i++) {
      const char = url.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // 转换为32位整数
    }
    return Math.abs(hash).toString(36)
  }

  /**
   * 清空所有缓存
   */
  clearCache() {
    // 清空内存缓存
    this.memoryCache.clear()
    
    // 清空localStorage缓存
    const keys = Object.keys(localStorage).filter(key => 
      key.startsWith(this.storagePrefix)
    )
    keys.forEach(key => localStorage.removeItem(key))
  }

  /**
   * 获取缓存统计信息
   * @returns {Object} 统计信息
   */
  getCacheStats() {
    const memoryCount = this.memoryCache.size
    const storageKeys = Object.keys(localStorage).filter(key => 
      key.startsWith(this.storagePrefix)
    )
    const storageCount = storageKeys.length
    const storageSize = this.getCurrentCacheSize()

    return {
      memory: {
        count: memoryCount
      },
      storage: {
        count: storageCount,
        size: Math.round(storageSize / 1024 / 1024 * 100) / 100 // MB
      }
    }
  }
}

// 创建单例实例
const imageCache = new ImageCache()

export default imageCache 